import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, Form, Row, Col, Container, Modal } from "react-bootstrap";
import { Icon, Loader as SemanticUILoader } from "semantic-ui-react";
import axios from "axios";

import CommonModal from "../../components/CommonModal";
import LearnerSelectionTable from "./LearnerSelectionTable";
import { selectUser } from "../../Redux/Actions/enrollmentsAction";

const buildLearnerKey = (row) => {
  if (!row) return "";
  const { user_id, first_name, last_name } = row;
  if (!user_id) return "";
  return `${user_id}:${first_name ?? ""} ${last_name ?? ""}`;
};

const FindLearnerModal = ({
  show,
  onClose,
  onConfirm,
  singleSearchField = false,
  accessToken,
}) => {
  const baseApiUrl = global.config.baseApiUrl;
  const companyId = global.config.companyId;

  const dispatch = useDispatch();

  const [searchLearner, setSearchLearner] = useState({
    firstName: "",
    lastName: "",
    id: "",
    searchText: "",
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasBeenSearched, setHasBeenSearched] = useState(false);
  const [selectedLearnerKey, setSelectedLearnerKey] = useState("");
  const [showAlert, setShowAlert] = useState({
    noTextAdded: false,
    noUserSelected: false,
  });

  const resetModalState = () => {
    setSearchLearner({ firstName: "", lastName: "", id: "", searchText: "" });
    setRows([]);
    setHasBeenSearched(false);
    setSelectedLearnerKey("");
    setShowAlert({ noTextAdded: false, noUserSelected: false });
  };

  useEffect(() => {
    if (show) {
      resetModalState();
    }
  }, [show]);

  const onChange = (key) => (e) => {
    const searchTerm = e.target.value;
    setSearchLearner((prev) => ({
      ...prev,
      [key]: searchTerm,
    }));
  };

  const enableAlert = (key) => {
    setShowAlert((prev) => ({ ...prev, [key]: true }));
  };

  const hideAlert = (key) => {
    setShowAlert((prev) => ({ ...prev, [key]: false }));
  };

  const onSearch = async () => {
    const firstName = searchLearner.firstName.trim();
    const lastName = searchLearner.lastName.trim();
    const id = searchLearner.id.trim();
    const searchText = searchLearner.searchText.trim();

    if (singleSearchField) {
      if (!searchText) {
        enableAlert("noTextAdded");
        setRows([]);
        setSelectedLearnerKey("");
        return;
      }
    } else if (!firstName && !lastName && !id) {
      enableAlert("noTextAdded");
      setRows([]);
      setSelectedLearnerKey("");
      return;
    }

    const isFord = companyId.includes("ford");

    // Ford backend supports a combined-criteria query that ANDs any subset of
    // {firstName, lastName, id (GPID/CDSID/GD/badge)}. Docebo uses search_text only.
    let payload;
    if (singleSearchField) {
      payload = {
        search_text: searchText,
        scanned: "N",
      };
    } else if (isFord) {
      payload = {
        firstName,
        lastName,
        id,
        scanned: "N",
      };
    } else {
      payload = {
        search_text: `${firstName} ${lastName} ${id}`.trim(),
        scanned: "N",
      };
    }

    if (accessToken) {
      payload.accessToken = accessToken;
    }

    try {
      setLoading(true);

      const res = await axios.post(`${baseApiUrl}/people`, payload);

      setRows(res.data.users || []);
    } catch (err) {
      console.error(err);
      setRows([]);
    } finally {
      setSelectedLearnerKey("");
      setHasBeenSearched(true);
      setLoading(false);
    }
  };

  const handleSelect = (row) => {
    const learnerKey = buildLearnerKey(row);
    setSelectedLearnerKey(learnerKey);
    // keep this for legacy consumers of enrollments.user
    dispatch(selectUser(learnerKey));
  };

  const handleConfirm = () => {
    if (!selectedLearnerKey) {
      enableAlert("noUserSelected");
      return;
    }

    if (typeof onConfirm === "function") {
      onConfirm(selectedLearnerKey);
    }

    resetModalState();
    onClose();
  };

  const columns = companyId.includes("ford")
    ? [
        { key: "gpid", label: "GPID (Person Number)" },
        { key: "username", label: "CDSID (Username)" },
        { key: "fullName", label: "Full Name" },
        { key: "email", label: "Email" },
      ]
    : [
        { key: "id", label: "User Id" },
        { key: "username", label: "Username" },
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "Manager", label: "Manager" },
      ];

  const formattedRows = useMemo(
    () =>
      companyId.includes("ford")
        ? rows.map((u) => ({
            gpid: u.user_id,
            username: u.username,
            fullName: u.name,
            email: u.email,
            ...u,
          }))
        : rows.map((u) => ({
            id: u.user_id,
            username: u.username,
            name:
              `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() ||
              u.fullname ||
              u.name ||
              "",
            email: u.email,
            Manager: u.is_manager ? "Yes" : "No",
            ...u,
          })),
    [companyId, rows],
  );

  return (
    <>
      <CommonModal
        show={show}
        onHide={onClose}
        title={
          <Modal.Title>
            <Icon name="search" /> Find Learner
          </Modal.Title>
        }
        footer={
          <>
            <Button variant="dark" onClick={onClose} disabled={loading}>
              Close
            </Button>
            <Button variant="success" onClick={handleConfirm}>
              Confirm
            </Button>
          </>
        }
        modalProps={{
          size: "lg",
          centered: true,
          backdrop: "static",
          className: "manage-roster-modal find-learner-modal",
        }}
      >
        <Container>
          <Row>
            <Col>
              <p className="mb-3">
                Find a learner by entering all or part of their ID, name, or organization.
              </p>
            </Col>
          </Row>

          <Form
            onSubmit={(e) => {
              e.preventDefault();
              if (!loading) {
                onSearch();
              }
            }}
          >
            {singleSearchField ? (
              <Row className="mb-3 px-3">
                <Col md={11} className="learner-field">
                  <Form.Control
                    placeholder="Search by name, username, or ID"
                    value={searchLearner.searchText}
                    onChange={onChange("searchText")}
                  />
                </Col>
                <Col md={1} className="px-0">
                  <Button
                    type="submit"
                    variant="primary"
                    className="w-100 btn standard-btn bold"
                    disabled={loading}
                  >
                    Search
                  </Button>
                </Col>
              </Row>
            ) : (
              <Row className="mb-3 px-3">
                <Col md={4} className="learner-field">
                  <Form.Control
                    placeholder="First Name"
                    value={searchLearner.firstName}
                    onChange={onChange("firstName")}
                  />
                </Col>
                <Col md={4} className="learner-field">
                  <Form.Control
                    placeholder="Last Name"
                    value={searchLearner.lastName}
                    onChange={onChange("lastName")}
                  />
                </Col>
                <Col md={3} className="learner-field">
                  <Form.Control
                    placeholder={companyId.includes("ford") ? "GPID / CDSID" : "ID"}
                    value={searchLearner.id}
                    onChange={onChange("id")}
                  />
                </Col>
                <Col md={1} className="px-0">
                  <Button
                    type="submit"
                    variant="primary"
                    className="w-100 btn standard-btn bold"
                    disabled={loading}
                  >
                    Search
                  </Button>
                </Col>
              </Row>
            )}
          </Form>

          {loading ? (
            <SemanticUILoader active inline="centered">
              Searching...
            </SemanticUILoader>
          ) : hasBeenSearched && formattedRows.length === 0 ? (
            <>
              <hr className="hr w-100 mt-4" />
              <div className="text-center font-italic">
                {singleSearchField
                  ? "No results found. Try refining your search."
                  : "No results found. Try refining your First Name, Last Name, or ID."}
              </div>
            </>
          ) : (
            formattedRows.length > 0 && (
              <>
                <hr className="hr w-100 mt-5 mb-4" />
                <LearnerSelectionTable
                  columns={columns}
                  rows={formattedRows}
                  onSelect={handleSelect}
                  selectedKey={selectedLearnerKey}
                  getRowKey={buildLearnerKey}
                />
              </>
            )
          )}
        </Container>
      </CommonModal>

      {/* Alert: no search text */}
      <CommonModal
        show={showAlert.noTextAdded}
        onHide={() => hideAlert("noTextAdded")}
        title={<Modal.Title>Alert</Modal.Title>}
        footer={
          <Button variant="dark" onClick={() => hideAlert("noTextAdded")} className="px-4">
            Ok
          </Button>
        }
        modalProps={{ centered: true, backdrop: false, className: "manage-roster-modal" }}
      >
        Please enter at least one item to search by.
      </CommonModal>

      {/* Alert: no user selected */}
      <CommonModal
        show={showAlert.noUserSelected}
        onHide={() => hideAlert("noUserSelected")}
        title={<Modal.Title>Alert</Modal.Title>}
        footer={
          <Button variant="dark" onClick={() => hideAlert("noUserSelected")} className="px-4">
            Ok
          </Button>
        }
        modalProps={{ centered: true, backdrop: false, className: "manage-roster-modal" }}
      >
        Please select a user before continuing.
      </CommonModal>
    </>
  );
};

export default FindLearnerModal;
